import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import 'react-table/react-table.css'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import {
  getSuppliersDocks,
  getSuppliersQueue,
} from 'src/services/suppliersQueue/suppliersQueueApi'
import { getNotReleasedReceivements } from 'src/services/receivement/receivementService'
import { AttendanceTab } from '../../components/AttendanceTab'
import { HistoryTab } from '../../components/HistoryTab'
import { LastUpdated } from '../../components/LastUpdated'
import { PrintInvoicesSection } from '../../components/PrintInvoicesSection'
import { PreQueueTab } from '../../components/PreQueueTab/PreQueueTab'
import { QueueTab } from '../../components/QueueTab'
import Tabs, { OptionsNames } from '../../components/Tabs'
import { useUpdateTab } from '../../hooks/useUpdateTab'
import * as S from './SupplierQueue.styles'
import { SupplierReleaseTab } from '../../components/SupplierReleaseTab'

function SupplierQueue(): JSX.Element {
  const [activeDocks, setActiveDocks] = useState([])
  const [receivementsQueue, setReceivementsQueue] = useState([])

  const [activeDocksFresh, setActiveDocksFresh] = useState([])
  const [receivementsQueueFresh, setReceivementsQueueFresh] = useState([])

  const [notReleasedReceivements, setNotReleasedReceivements] = useState([])

  const [activeTab, setActiveTab] = useState(OptionsNames.Attendance)
  const activeTabRef = useRef(activeTab)
  const { fc, validateFc } = useFulfillmentCenter()

  const {
    loading, handleUpdate,
  } = useUpdateTab()

  useEffect(() => {
    validateFc()
  }, [validateFc])

  const getReceivementsQueue = useCallback(() => {
    handleUpdate(
      () => getSuppliersQueue(fc.id, false),
      (response) => setReceivementsQueue(response.data.receivements_queue),
    )
  }, [fc.id, handleUpdate])

  const getReceivementsQueueFresh = useCallback(() => {
    handleUpdate(
      () => getSuppliersQueue(fc.id, true),
      (response) => setReceivementsQueueFresh(response.data.receivements_queue),
    )
  }, [fc.id, handleUpdate])

  const getActiveDocks = useCallback(() => {
    handleUpdate(
      () => getSuppliersDocks(fc.id, false),
      (response) => setActiveDocks(response.data.active_receivements),
    )
  }, [fc.id, handleUpdate])

  const getActiveDocksFresh = useCallback(() => {
    handleUpdate(
      () => getSuppliersDocks(fc.id, true),
      (response) => setActiveDocksFresh(response.data.active_receivements),
    )
  }, [fc.id, handleUpdate])

  const handleGetNotReleasedReceivements = useCallback(() => {
    handleUpdate(
      () => getNotReleasedReceivements(fc.id),
      (response) => setNotReleasedReceivements(response.data),
    )
  }, [fc.id, handleUpdate])

  const updateCurrentTab = useCallback(() => {
    const updateData = {
      [OptionsNames.Attendance]: () => getActiveDocks(),
      [OptionsNames.Queue]: () => getReceivementsQueue(),
      [OptionsNames.AttendanceFresh]: () => getActiveDocksFresh(),
      [OptionsNames.QueueFresh]: () => getReceivementsQueueFresh(),
      [OptionsNames.SupplierRelease]: () => handleGetNotReleasedReceivements(),
    }

    updateData[activeTabRef.current]?.()
  }, [
    getActiveDocks,
    getActiveDocksFresh,
    getReceivementsQueue,
    getReceivementsQueueFresh,
    handleGetNotReleasedReceivements,
  ])

  const updateActiveTab = (newState) => {
    activeTabRef.current = newState
    setActiveTab(newState)
    updateCurrentTab()
  }

  useEffect(() => {
    updateCurrentTab()

    const interval = setInterval(() => {
      updateCurrentTab()
    }, 60000)

    const updateTimes = setInterval(() => {
      if (activeDocks.length) setActiveDocks(Array.from(activeDocks))
      if (receivementsQueue.length) { setReceivementsQueue(Array.from(receivementsQueue)) }
    }, 1000)

    return () => {
      clearInterval(interval)
      clearInterval(updateTimes)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCurrentTab])

  const resolveLastUpdateInfo = () => ![OptionsNames.History, OptionsNames.ReturnInvoices].includes(activeTab) && <LastUpdated loading={loading} />

  return (
    <S.Wrapper data-testid='supplier-tabs'>
      {activeTab !== OptionsNames.ReturnInvoices && (
      <S.SupplierActions>
        <S.ReceiveSupplierButton variant='contained' size='large' type='button' onClick={() => window.location.replace('/suppliers/receivement')}>Receber</S.ReceiveSupplierButton>
      </S.SupplierActions>
      )}
      <Tabs activeTab={activeTab} onTabChange={updateActiveTab} />
      {resolveLastUpdateInfo()}

      {activeTab === OptionsNames.Attendance && (
        <AttendanceTab
          activeDocks={activeDocks}
          getActiveDocks={getActiveDocks}
        />
      )}

      {activeTab === OptionsNames.AttendanceFresh && (
        <AttendanceTab
          activeDocks={activeDocksFresh}
          getActiveDocks={getActiveDocksFresh}
        />
      )}

      {activeTab === OptionsNames.Queue && (
        <QueueTab
          receivementsQueue={receivementsQueue}
          updateCurrentTab={updateCurrentTab}
        />
      )}

      {activeTab === OptionsNames.PreQueue && (
        <PreQueueTab />
      )}

      {activeTab === OptionsNames.QueueFresh && (
        <QueueTab
          receivementsQueue={receivementsQueueFresh}
          updateCurrentTab={updateCurrentTab}
        />
      )}

      {activeTab === OptionsNames.ReturnInvoices && (
        <PrintInvoicesSection />
      )}

      {activeTab === OptionsNames.History && (
        <HistoryTab fulfillmentCenterId={fc.id} />
      )}

      {activeTab === OptionsNames.SupplierRelease && (
        <SupplierReleaseTab
          notReleasedReceivements={notReleasedReceivements}
        />
      )}
    </S.Wrapper>
  )
}

export default SupplierQueue
